import React, { useState } from 'react';
import {
  Card,
  Typography,
  Box,
  Chip,
  IconButton,
  Collapse,
  LinearProgress,
  Button,
  Avatar,
  AvatarGroup,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Link } from 'react-router-dom';
import { Task, Role } from '../types';
import { getPriorityColor, getRoleColor, calculateDaysRemaining, formatDate } from '../utils/helpers';
import { useProjectStore } from '../store/useProjectStore';
import SubtaskList from './SubtaskList';
import RoleDetailModal from './RoleDetailModal';

const StyledCard = styled(Card)<{ isCollapsed?: boolean }>(({ theme, isCollapsed }) => ({
  backgroundColor: '#ffffff',
  borderRadius: theme.spacing(1.5),
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  overflow: 'visible',
  height: 'auto',
  position: 'relative',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
    transform: 'translateY(-2px)',
  },
}));

const CardContent = styled(Box)<{ isCollapsed?: boolean }>(({ theme, isCollapsed }) => ({
  padding: isCollapsed ? theme.spacing(2.5) : theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    padding: isCollapsed ? theme.spacing(2) : theme.spacing(2.5),
  },
}));

const CollapseToggle = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1.5),
  right: theme.spacing(1.5),
  width: 32,
  height: 32,
  backgroundColor: '#f8fafc',
  border: '1px solid #e2e8f0',
  '&:hover': {
    backgroundColor: '#f1f5f9',
    borderColor: '#cbd5e1',
  },
}));

const CollapsedView = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  flexWrap: 'wrap',
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(1.5),
  },
}));

const TaskTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.125rem',
  fontWeight: 600,
  color: '#1a202c',
  lineHeight: 1.4,
  marginRight: theme.spacing(1),
}));

const MetaInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  flexWrap: 'wrap',
  marginTop: theme.spacing(1.5),
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(1),
  },
}));

const MetaItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  fontSize: '0.875rem',
  color: '#64748b',
}));

const ProgressIndicator = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginLeft: 'auto',
  minWidth: 120,
  [theme.breakpoints.down('sm')]: {
    marginLeft: 0,
    width: '100%',
    marginTop: theme.spacing(1),
  },
}));

const TaskDescription = styled(Box)(({ theme }) => ({
  fontSize: '0.875rem',
  lineHeight: 1.6,
  color: '#475569',
  backgroundColor: '#f8fafc',
  border: '1px solid #e2e8f0',
  borderRadius: theme.spacing(1),
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2.5),
}));

const RolesSection = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2.5),
  paddingTop: theme.spacing(2.5),
  borderTop: '1px solid #e2e8f0',
}));

const SectionLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 600,
  color: '#94a3b8',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: theme.spacing(1),
}));

const StyledProgressBar = styled(LinearProgress)(({ theme }) => ({
  height: 6,
  backgroundColor: '#e2e8f0',
  borderRadius: 3,
  '& .MuiLinearProgress-bar': {
    borderRadius: 3,
    background: 'linear-gradient(90deg, #3b82f6, #8b5cf6)',
  },
}));

const ActionsBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(3),
  paddingTop: theme.spacing(3),
  borderTop: '1px solid #e2e8f0',
  flexWrap: 'wrap',
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(1),
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 500,
  borderRadius: theme.spacing(1),
  padding: theme.spacing(0.75, 2),
  fontSize: '0.875rem',
  boxShadow: 'none',
  '&:hover': {
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
}));

const DiscussionButton = styled(StyledButton)(({ theme }) => ({
  color: '#64748b',
  '&:hover': {
    backgroundColor: '#f8fafc',
  },
}));

interface TaskCardProps {
  task: Task;
  onOpenDiscussion: (taskId: string, subtaskId: string | null) => void;
}

const TaskCard: React.FC<TaskCardProps> = ({ task, onOpenDiscussion }) => {
  const { toggleTaskCollapse } = useProjectStore();
  const [localCollapsed, setLocalCollapsed] = useState(task.isCollapsed || false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [roleModalOpen, setRoleModalOpen] = useState(false);

  const handleToggleCollapse = () => {
    setLocalCollapsed(!localCollapsed);
    toggleTaskCollapse(task.id);
  };

  const handleRoleClick = (role: Role) => {
    setSelectedRole(role);
    setRoleModalOpen(true);
  };

  const handleCloseRoleModal = () => {
    setRoleModalOpen(false);
    setSelectedRole(null);
  };

  const priorityColor = getPriorityColor(task.priority);
  const daysRemaining = calculateDaysRemaining(task.expectedDate);

  const calculateTaskProgress = () => {
    const totalSubtasks = task.subtasks.length;
    if (totalSubtasks === 0) return 0;
    
    const completedSubtasks = task.subtasks.filter(st => st.status === 'completed').length;
    return Math.round((completedSubtasks / totalSubtasks) * 100);
  };

  const progress = calculateTaskProgress();

  // Get unique assignees
  const allAssignees = Array.from(new Set(
    task.subtasks.flatMap(st => st.assignees || [])
  )).slice(0, 3);

  return (
    <StyledCard isCollapsed={localCollapsed}>
      <CollapseToggle size="small" onClick={handleToggleCollapse}>
        {localCollapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
      </CollapseToggle>

      <CardContent isCollapsed={localCollapsed}>
        {/* Collapsed View */}
        <Collapse in={localCollapsed} timeout="auto">
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <TaskTitle variant="h6">{task.title}</TaskTitle>
              <Chip
                label={task.priority === 'high' ? '高' : task.priority === 'medium' ? '中' : '低'}
                size="small"
                sx={{
                  height: 22,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  backgroundColor: priorityColor.bg,
                  color: priorityColor.color,
                  border: `1px solid ${priorityColor.border}`,
                }}
              />
            </Box>
            
            <MetaInfo>
              <MetaItem>
                <Typography variant="caption" sx={{ fontWeight: 500 }}>PM:</Typography>
                <Typography variant="caption">{task.pmName}</Typography>
              </MetaItem>
              
              <MetaItem>
                <Typography variant="caption" sx={{ fontWeight: 500 }}>期限:</Typography>
                <Typography variant="caption">
                  {daysRemaining > 0 ? `${daysRemaining}天` : '已逾期'}
                </Typography>
              </MetaItem>
              
              <MetaItem>
                <AvatarGroup max={3} sx={{ '& .MuiAvatar-root': { width: 24, height: 24, fontSize: '0.75rem' } }}>
                  {allAssignees.map(assignee => (
                    <Avatar key={assignee} sx={{ bgcolor: '#3b82f6' }}>
                      {assignee.charAt(0)}
                    </Avatar>
                  ))}
                </AvatarGroup>
              </MetaItem>
              
              <ProgressIndicator>
                <StyledProgressBar variant="determinate" value={progress} sx={{ flex: 1 }} />
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e293b', minWidth: 35, textAlign: 'right' }}>
                  {progress}%
                </Typography>
              </ProgressIndicator>
            </MetaInfo>
          </Box>
        </Collapse>

        {/* Expanded View */}
        <Collapse in={!localCollapsed} timeout="auto">
          <Box>
            <Box sx={{ mb: 2 }}>
              <Chip
                label={`PM: ${task.pmName}`}
                size="small"
                sx={{ 
                  height: 24,
                  backgroundColor: '#dbeafe', 
                  color: '#1e40af', 
                  fontWeight: 600,
                  fontSize: '0.75rem',
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
              <TaskTitle variant="h5" sx={{ fontSize: '1.25rem' }}>
                {task.title}
              </TaskTitle>
              <Chip
                label={task.priority === 'high' ? '高優先' : task.priority === 'medium' ? '中優先' : '低優先'}
                size="small"
                sx={{
                  height: 26,
                  backgroundColor: priorityColor.bg,
                  color: priorityColor.color,
                  border: `1px solid ${priorityColor.border}`,
                  fontWeight: 600,
                  fontSize: '0.8125rem',
                }}
              />
            </Box>

            <TaskDescription>
              {task.description}
            </TaskDescription>

            <MetaInfo sx={{ mb: 2 }}>
              <MetaItem>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>開始:</Typography>
                <Typography variant="body2">{formatDate(task.startDate)}</Typography>
              </MetaItem>
              <MetaItem>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>預計:</Typography>
                <Typography variant="body2">{formatDate(task.expectedDate)}</Typography>
              </MetaItem>
              <MetaItem>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>剩餘:</Typography>
                <Typography variant="body2" sx={{ color: daysRemaining > 7 ? 'inherit' : '#ef4444' }}>
                  {daysRemaining > 0 ? `${daysRemaining}天` : '已逾期'}
                </Typography>
              </MetaItem>
            </MetaInfo>

            <Box sx={{ mb: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, color: '#475569' }}>
                  整體進度
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                  {progress}%
                </Typography>
              </Box>
              <StyledProgressBar variant="determinate" value={progress} />
            </Box>

            <RolesSection>
              <SectionLabel>職能分配</SectionLabel>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {task.roles.map((role) => (
                  <Chip
                    key={role}
                    label={role}
                    size="small"
                    sx={{
                      height: 26,
                      bgcolor: getRoleColor(role),
                      color: 'white',
                      fontWeight: 500,
                      fontSize: '0.8125rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                      },
                    }}
                    onClick={() => handleRoleClick(role)}
                  />
                ))}
              </Box>
            </RolesSection>

            <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #e2e8f0' }}>
              <SubtaskList
                taskId={task.id}
                task={task}
                subtasks={task.subtasks}
                onOpenDiscussion={(subtaskId: string) => onOpenDiscussion(task.id, subtaskId)}
              />
            </Box>

            <ActionsBar>
              <Button
                component={Link as any}
                to={`/task/${task.id}`}
                variant="contained"
                size="small"
                startIcon={<VisibilityIcon fontSize="small" />}
                sx={{ 
                  textTransform: 'none',
                  fontWeight: 500,
                  borderRadius: 1,
                  padding: '6px 16px',
                  fontSize: '0.875rem',
                  boxShadow: 'none',
                  '&:hover': {
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                  },
                }}
              >
                查看詳情
              </Button>
              <Button
                component={Link as any}
                to={`/task/${task.id}/edit`}
                variant="outlined"
                size="small"
                startIcon={<EditIcon fontSize="small" />}
                sx={{ 
                  textTransform: 'none',
                  fontWeight: 500,
                  borderRadius: 1,
                  padding: '6px 16px',
                  fontSize: '0.875rem',
                  boxShadow: 'none',
                  '&:hover': {
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                  },
                }}
              >
                編輯
              </Button>
              <DiscussionButton
                variant="text"
                size="small"
                startIcon={<ChatBubbleOutlineIcon fontSize="small" />}
                onClick={() => onOpenDiscussion(task.id, null)}
              >
                討論
                {task.discussionCount > 0 && (
                  <Chip 
                    label={task.discussionCount} 
                    size="small" 
                    sx={{ 
                      ml: 0.75, 
                      height: 20,
                      backgroundColor: '#f1f5f9',
                      color: '#475569',
                      fontWeight: 600,
                    }} 
                  />
                )}
              </DiscussionButton>
            </ActionsBar>
          </Box>
        </Collapse>
      </CardContent>

      {/* Role Detail Modal */}
      {selectedRole && (
        <RoleDetailModal
          open={roleModalOpen}
          onClose={handleCloseRoleModal}
          role={selectedRole}
        />
      )}
    </StyledCard>
  );
};

export default TaskCard; 